import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { catchError, first, map, switchMap } from 'rxjs/operators';
import { Utilities } from '../class/utilities';
import { CashTitle, Receive } from '../interface/cash-title';
import { Invoice } from '../interface/invoice';
import { NFe } from '../interface/NF-e';
import { NFCe } from '../interface/NFC-e';
import { NFSe, RPS } from '../interface/NFS-e';
import { Finance } from '../interface/ro-finance';
import { environment } from '../../environments/environment';
import { ApiPaginatedTitleQueryParams, ApiPaginatedTitlesResponse, PaginationService } from './pagination.service';
import { Observable, firstValueFrom, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { DataService } from './data.service';
import { API_ERRORS } from '../shared/lists/errors';
import { ObjectId } from '../shared/type-aliases/object-id';
import { IsoString } from '../shared/type-aliases/iso-string';
import { Address } from '../interface/address';
import { Company } from '../interface/company';
import { Supplier } from '../interface/supplier';
import moment, { Moment } from 'moment';
import { MkgOS } from '../class/mkg-os';
import { CashTitleHandlerService } from './cash-title-handler.service';
import { SessionError } from './session/mkg-session';
import { ConfirmationDialogComponent } from '../component/dialog/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { LayoutService } from './layout.service';
import { SessionTitleService } from './session/session-title.service';
import { PaymentCondition } from '../interface/paymentCondition';
import { Operation } from '../interface/operation';
import { LOG_REPORTER_KEYS, LogReporterService } from './log-reporter.service';
import { BOOLEAN_NUMBER } from '../shared/common-enums/boolean-number';
import { CashTitleLogService } from './cash-title-log.service';
import { CASH_TITLE_CREATED_BY, CASH_TITLE_MOVEMENT_TYPE, CreateCashTitleMovimentationParamethers } from '../interface/cash-log-interfaces';
import { HolidayService } from '@app/component/page/company/records/holidays/services/holiday.service';
import { NF_response } from '@app/interface/NF-response';
import { ApplyLowOptions } from '@app/component/page/company/financial/apply-low/confirm-apply-low/confirm-apply-low.component';
import { IStatusTitleApiIn } from '@app/component/page/company/records/status-title/models/status-title.model';

export interface TitlesTotal {
  balancePayTotal: number;
  balanceReceiveTotal: number;
  valuePayTotal: number;
  valueReceiveTotal: number;
  paidTotal: number;
  receivedTotal: number;
  /** Company id */
  _id?: ObjectId;
}

export interface PaginatedTitle {
  readonly available?: 0 | 1;
  readonly _id?: ObjectId;
  readonly company?: ObjectId;
  readonly createdAt: IsoString;
  readonly updatedAt: IsoString;
  readonly seq: number;
  readonly __v: number;

  bank?: {
    code: string;
    name: string;
    operation: string;
    account: string;
  }
  companyCode?: string;
  idOS?: string;
  companyOrder?: string;
  orderId?: string;
  type?: ObjectId;
  titleTypePopulate: {
    description: string;
    /** 
     * true = pay
     * false = receive
     */
    payReceive: boolean;
  }

  clientTmp?: string;

  qtdaPortion?: number;
  flag?: string;

  readonly supplier_client: {
    _id: ObjectId,
    name: string;
    document: string;
    vehicles: Array<string>;
    available: 0 | 1;
    gender: number;
    lang: number;
    maritalStatus: number;
    blocked: boolean;
    subscribed: boolean;
    kind: any;
    updateHistory: Array<any>;
    createdAt: IsoString;
    updatedAt: IsoString;
    rg: string;
    phone1: string;
    phone2: string;
    address: Address;
    lastUpdate: IsoString;
    type: any;
    documentType: number;
    hash: string,
    marketing: number,
    __v: number,
    fancyName?: string,
    birthday?: IsoString,
    taxpayer?: boolean,
    retCOFINS?: boolean,
    retCSLL?: boolean,
    retINSS?: boolean,
    retIRPJ?: boolean,
    retPIS?: boolean,
    lead?: any,
    email?: string,
    creditLimit?: number,
    cardAdmin?: Supplier['cardAdmin']
  },

  invoiceNumber?: string;
  paymentCondition: ObjectId;
  expenseType?: ObjectId;
  value: number;
  parcel: number;
  observation: string;
  balance: number;
  provisional?: BOOLEAN_NUMBER;
  paymentConditionPopulate?: PaymentCondition;

  /**
 * When the Cash Titles are created from OS or Order, the status is inactive (0).
 * So, when OS is closed, all titles are updated to active (status = 1).
 * Whem a order has its status changed from "ORÇAMENTO"(1) to "PEDIDO"(2), his titles also
 * must be updated to active status.
 *
 * If the title is created from invoice, the status is active directly (1).
 *
 * Whan the title cannot be deleted (already have received lows), the status is seetted to canceled(2)
 *
 * - 0 - inactive
 * - 1 - active
 * - 2 - canceled
 * */
  status: 0 | 1 | 2;
  received: Receive[];
  /** no get por id isso é boolean, no get paginado é number? */
  adiantamento: number;
  expirationDate?: IsoString;
  movementDate: IsoString;
  rateTitle?: number;

  /** Local only */
  cnpj: string;
  fees?: number;
  penaltyFee?: number;
  rate?: number;
  discount?: number;
  /** local only */
  toPay?: number;
  statusTitle?: IStatusTitleApiIn;
  boleto?: {
    _id?: string;
    boleto_link?: string;
    group_link?: string;
    status?: string;
  };

  currentInstallment?: number;
  totalInstallments?: number;
}


@Injectable({
  providedIn: 'root'
})
export class CashTitleService {

  private now = new Date();
  cashTitles: CashTitle[];
  private _logReporterService = inject(LogReporterService);

  constructor(
    private dataService: DataService,
    private http: HttpClient,
    private _dataService: DataService,
    private _cashTitleHandlerService: CashTitleHandlerService,
    private _sessionTitleService: SessionTitleService,
    private _dialog: MatDialog,
    private _layout: LayoutService,
    private _cashTitleLogService: CashTitleLogService,
    private _holidayService: HolidayService
  ) { }

  /**
   *  @deprecated Try to use `getPaginated()`, `findOStitles()` or `findOrderTitles()` instead
   */
  async getAll(cnpj: string, skipInactives = true): Promise<CashTitle[]> {
    const url = `${environment.mkgoURL}/api/v1/title`;
    const header = await firstValueFrom(this.dataService.httpOptions(cnpj));
    await this._cashTitleHandlerService.getData();
    const resp = await firstValueFrom(this.http.get<{ titles: any[] }>(url, header).pipe(
      first(),
      tap((resp) => {
        const wrongTitles = [];
        for (const title of resp.titles) {
          const ids = new Set(title.received.reduce((arr, received) => arr.concat(received._id), []));
          if (ids.size !== title.received.length) {
            wrongTitles.push(title)
          }
        }
        if (wrongTitles.length) {
          console.warn('Alguns títulos possuem baixas com o mesmo id', wrongTitles)
        }
      }),
    ));
    let titles: CashTitle[] = [];
    for (let title of resp.titles) {
      const titleApp = await this._cashTitleHandlerService.complyAPP(title);
      titles.unshift(titleApp)
    }
    this.cashTitles = skipInactives ? titles.filter(t => t.status !== 0) : titles;
    return titles;
  }

  getTitlesBeforeExpirationDateStart(cnpj: string, expirationDate: Moment): Observable<PaginatedTitle[]> {
    const url = `${environment.mkgoURL}/api/v1/title/expiration-date`;
    const params = new HttpParams().append('expirationDate', expirationDate.toISOString());
    return this.dataService.httpOptions(cnpj, params)
      .pipe(switchMap(header => this.http.get<PaginatedTitle[]>(url, header)));
  }

  /**
   * Attention: this method DON'T return inactive titles
   *
   * if you want to find inactive titles of an OS, use `findOStitles()` instead
   *
   * if you want to find inactive titles of an Order, use `findOrderTitles()` instead
   *
   * @param matrix If the company have matrix, pass it to get name of supplier from updateHistory
   */
  getPaginated(cnpj: string, apiParams: ApiPaginatedTitleQueryParams, matrix?: Company): Observable<ApiPaginatedTitlesResponse<PaginatedTitle>> {
    const url = `${environment.mkgoURL}/api/v1/title/pages`;
    let params = PaginationService.getParams(apiParams);
    return this._dataService.httpOptions(cnpj, params).pipe(
      switchMap(options => this.http.get<ApiPaginatedTitlesResponse<PaginatedTitle>>(url, options)),
      first(),
      switchMap(resp => {
        // the API return 100 docs by defult (even if all=1), so if we want all docs we must call it again with param `limit`
        if (apiParams.all && resp.hasNextPage) {
          apiParams.limit = resp.totalDocs;
          params = PaginationService.getParams(apiParams);
          return this._dataService.httpOptions(cnpj, params).pipe(
            switchMap(options2 => this.http.get<ApiPaginatedTitlesResponse<PaginatedTitle>>(url, options2)),
            first()
          )
        } else {
          return of(resp)
        }
      }),
      map(resp => {
        if (!resp.totals) {
          resp.totals = {
            _id: "",
            paidTotal: 0,
            receivedTotal: 0,
            valuePayTotal: 0,
            valueReceiveTotal: 0,
            balancePayTotal: 0,
            balanceReceiveTotal: 0,
          };
        }
    
        /** @TODO pass it to backend */
        resp.docs.map(title => {
          if (matrix && title.supplier_client['updateHistory']) {
            const clientHistory = title.supplier_client['updateHistory'].find(hist => hist.company === matrix.id);
            if (clientHistory) {
              title.supplier_client.name = clientHistory.name;
            }
          }
          title.cnpj = cnpj;
          return title
        });
        return resp;
      }),
      catchError(err => {
        /** If no one data is found, the API will return a error */
        if (err.error.error === API_ERRORS.notFound) {
          return of<ApiPaginatedTitlesResponse<PaginatedTitle>>({
            docs: <PaginatedTitle[]>[],
            hasNextPage: false,
            hasPrevPage: false,
            limit: 0,
            nextPage: null,
            page: 0,
            pagingCounter: 0,
            prevPage: null,
            totalDocs: 0,
            totalPages: 0,
            totals: {
              balancePayTotal: 0,
              balanceReceiveTotal: 0,
              paidTotal: 0,
              receivedTotal: 0,
              valuePayTotal: 0,
              valueReceiveTotal: 0,
              _id: ""
            }
          })
        } else {
          throw err;
        }
      }),
      map(resp => {
        resp.docs.map(t => {
          t.cnpj = cnpj || this.dataService.company.cnpj;
          const received = t.received;
          t.fees = received.reduce((sum, low) => sum + (low.fees || 0), 0);
          t.penaltyFee = received.reduce((sum, low) => sum + (low.penaltyFee || 0), 0);
          t.rate = received.reduce((sum, low) => sum + (low.rate || 0), 0);
          t.discount = received.reduce((sum, low) => sum + (low.discount || 0), 0);
          t.rateTitle = t.rateTitle ?? 0
        });
        return resp
      })
    );
  }

  async getById(id: string): Promise<CashTitle> {
    const url = `${environment.mkgoURL}/api/v1/title/${id}`;
    const header = await firstValueFrom(this.dataService.httpOptions(false));
    const resp = await firstValueFrom(this.http.get<any>(url, header)
      .pipe(
        catchError(async error => {

          if (error.status === 400 && (error.error as SessionError).userId) {
            const sessionError = error.error as SessionError;
            this._layout.loader = false;
            const result = await firstValueFrom(this._dialog.open(ConfirmationDialogComponent, {
              data: {
                text: "TITLE_IN_USE",
                param: sessionError,
                uniqueAction: false
              }
            }).afterClosed());
            if (result) {
              const os = await firstValueFrom(this._sessionTitleService.closeSession(id, sessionError._id));
              return os;
            }
          }
          throw error;
        })
      ));
    await this._cashTitleHandlerService.getData();
    const title = this._cashTitleHandlerService.complyAPP(resp);
    return title;
  }

  /**
   * @param personId ID of Client or Supplier
   * @returns an array of advance-titles
   */
  async getAdvancesOfPerson(personId: ObjectId): Promise<CashTitle[]> {
    const url = `${environment.mkgoURL}/api/v1/title/client/${personId}`;
    const options = await firstValueFrom(this.dataService.httpOptions(false));
    return firstValueFrom(this.http.get(url, options).pipe(
      first(),
      map((arr: any[]) => arr.map(obj => this._cashTitleHandlerService.complyAPP(obj)))
    ));
  }

  /** 
   * @param cashLogeExtraData - don't send title as a attribute here, have a single param to it in this function. The creation company will always be the logged company
   **/
  async create(title: CashTitle, cnpj: string, cashLogeExtraData: Partial<CreateCashTitleMovimentationParamethers>): Promise<string> {
    const url = `${environment.mkgoURL}/api/v1/title`;
    const header = await firstValueFrom(this.dataService.httpOptions(cnpj));
    const body = JSON.stringify(this._cashTitleHandlerService.complyAPI(title));
    let resp: { id: ObjectId; };
    let titleError: any;

    try {
      resp = await firstValueFrom(this.http.post<{ id: ObjectId }>(url, body, header));
      title._id = resp.id;
    } catch (error) {
      console.error(error);
      titleError = error;
    } finally {
      try {
        const {
          isSupplier,
          origin,
          invoiceId,
          movementType,
          targetCompany,
          valueChanged,
          renegociationTitlesId,
          triggerFromAdmCardCreation,
          advanceTargetId,
          advancesUsed
        } = cashLogeExtraData;

        await this._cashTitleLogService.create({
          title,
          triggerFromAdmCardCreation,
          origin: titleError ? CASH_TITLE_CREATED_BY.ERROR_AT_TITLE_GENERATION : origin,
          creationCompany: this.dataService.company.id,
          isSupplier,
          movementType,
          targetCompany,
          userId: this.dataService.user.id,
          invoiceId,
          valueChanged,
          renegociationTitlesId,
          titleErro: titleError,
          advancesUsed,
          advanceTargetId
        });
      } catch (error) {
        console.error(error);
      }
    }

    if (titleError) {
      throw titleError;
    }

    return resp.id;
  }

  /** 
   * @param cashLogeExtraData - don't send title as a attribute here, have a single param to it in this function. The creation company will always be the logged company
   **/
  async update(title: CashTitle | PaginatedTitle, cnpj: string, cashLogeExtraData: Partial<CreateCashTitleMovimentationParamethers> = {}): Promise<{ id: string }> {
    const url = `${environment.mkgoURL}/api/v1/title/${title._id}`;
    const header = await firstValueFrom(this.dataService.httpOptions(cnpj));
    const body = this._cashTitleHandlerService.complyAPI(title);
    let resp: { id: ObjectId; };
    let titleError: any;

    try {
      resp = await firstValueFrom(this.http.put<{ id: string }>(url, body, header));
    } catch (error) {
      console.error(error);
      titleError = error;
    } finally {
      try {
        const {
          isSupplier,
          titleBeforeChanges,
          origin,
          invoiceId,
          movementType,
          targetCompany,
          valueChanged,
          renegociationTitlesId,
          osId,
          orderId,
          invoiceNumber,
          advanceTargetId,
          advancesUsed,
        } = cashLogeExtraData;

        await this._cashTitleLogService.create({
          title,
          origin,
          creationCompany: this.dataService.company.id,
          isSupplier: isSupplier,
          movementType,
          targetCompany,
          userId: this.dataService.user.id,
          invoiceId,
          valueChanged,
          renegociationTitlesId,
          titleBeforeChanges,
          invoiceNumber,
          orderId,
          osId,
          titleErro: titleError,
          advancesUsed,
          advanceTargetId
        });
      } catch (error) {
        console.error(error);
      }
    }

    if (titleError) {
      throw titleError;
    }

    return resp;
  }

  async cancelBoletoByTitle(titleId: string): Promise<void> {
    const url = `${environment.mkgoURL}/api/v1/title/${titleId}/cancel-boleto`;
    const header = await firstValueFrom(this.dataService.httpOptions(false));
    await firstValueFrom(this.http.post<void>(url, {}, header));
  }

  async filter(
    args: {
      range?: {
        startDate?: string,
        endDate?: string
      }
      forceReceived?: boolean,
      totalByCondition?: boolean,
      balanceOtherThan?: number
    }
  ) {
    let url = `${environment.mkgoURL}/api/v1/title/filter`;
    let params = new HttpParams();

    if (args.range) {
      if (args.range.startDate) {
        params = params.append('startDate', args.range.startDate)
      }
      if (args.range.endDate) {
        params = params.append('endDate', args.range.endDate)
      }
    }

    if (args.hasOwnProperty('forceReceived')) {
      params = params.append('forceReceived', `${args.forceReceived}`)
    }
    if (args.hasOwnProperty('totalByCondition')) {
      params = params.append('totalByCondition', `${args.totalByCondition}`)
    }
    if (Number.isFinite(args.balanceOtherThan)) {
      params = params.append('balanceOtherThan', `${args.balanceOtherThan}`)
    }

    const header = await firstValueFrom(this.dataService.httpOptions(false));
    header['params'] = params;
    return firstValueFrom(this.http.get<{
      received: Receive,
      supplier_client: ObjectId,
      type: ObjectId
    }[]
    >(url, header).pipe(
      map((titles) => titles.map((t: any) => {
        t.received = [t.received];
        return this._cashTitleHandlerService.complyAPP(t)
      }))
    ));
  }

  public async delete(id: string, cnpj: string, cashLogeExtraData: Partial<CreateCashTitleMovimentationParamethers>) {
    const url = `${environment.mkgoURL}/api/v1/title/${id}`;
    const header = await firstValueFrom(this.dataService.httpOptions(cnpj));
    let resp: Promise<Object>;
    let titleError: any;

    try {
      resp = firstValueFrom(this.http.delete(url, header));
    } catch (error) {
      console.error(error);
      titleError = error;
    } finally {
      try {
        const {
          isSupplier,
          titleBeforeChanges,
          origin,
          invoiceId,
          movementType,
          targetCompany,
          valueChanged,
          renegociationTitlesId,
          osId,
          orderId,
          invoiceNumber,
          title,
          advanceTargetId,
          advancesUsed
        } = cashLogeExtraData;

        await this._cashTitleLogService.create({
          title,
          origin,
          creationCompany: this.dataService.company.id,
          isSupplier,
          movementType,
          targetCompany,
          userId: this.dataService.user.id,
          invoiceId,
          valueChanged,
          renegociationTitlesId,
          titleBeforeChanges,
          invoiceNumber,
          orderId,
          osId,
          advancesUsed,
          advanceTargetId,
          titleErro: titleError
        });
      } catch (error) {
        console.error(error);
      }
    }

    if (titleError) {
      throw titleError;
    }

    return resp;
  }

  /**
   * @returns The titles of OS (including inactives)
   */
  public findOsTitles(idOS: string, codeSystem: string | number, cnpj: string) {
    const url = `${environment.mkgoURL}/api/v1/title/osv2`;
    const params = PaginationService.getParams({
      idOS,
      companyCode: codeSystem
    });

    return this._dataService.httpOptions(cnpj, params).pipe(
      switchMap(options => this.http.get<{ titles: CashTitle[] }>(url, options)),
      first(),
      map(r => r.titles),
      catchError(err => {
        /** If no one data is found, the API will return a error */
        if (err.error.error === API_ERRORS.notFound) {
          return of([] as Array<CashTitle>)
        } else {
          this._logReporterService.errorLogger(`CashtitleService.findOsTitles(""${idOS}, ${codeSystem})`, LOG_REPORTER_KEYS.INVOICE).call(this, err);
          throw err;
        }
      })
    )
  }

  /**
   * @returns The titles of Order (including inactives)
   */
  public findOrderTitles(orderId: string, companyOrder: string | number, cnpj: string) {
    const url = `${environment.mkgoURL}/api/v1/title/osv3`;
    const params = PaginationService.getParams({
      orderId,
      companyOrder
    });

    return this._dataService.httpOptions(cnpj, params).pipe(
      switchMap(options => this.http.get<{ titles: CashTitle[] }>(url, options)),
      first(),
      map(r => r.titles),
      catchError(err => {
        /** If no one data is found, the API will return a error */
        if (err.error.error === API_ERRORS.notFound) {
          return of([] as Array<CashTitle>)
        } else {
          throw err;
        }
      })
    )
  }

  getTotals() {
    const url = `${environment.mkgoURL}/api/v1/title/totals`;
    return this._dataService.httpOptions(false).pipe(
      switchMap((options) => this.http.get<TitlesTotal>(url, options)))
  }

  public getExpirationDate(days: number, from: Date = new Date()): Observable<Date> {
    const milisseconds = days * Utilities.MS_DAY;
    let due = new Date(from.getTime() + milisseconds)

    return this._holidayService.getNextBusinessDay(due).pipe(
      map(resp => {
        if (resp.nextBusinessDay) {
          console.log('Ajustando data de vencimento para o próximo dia útil:', resp.nextBusinessDay);
          return new Date(resp.nextBusinessDay);
        }
        return due;
      }),
      catchError(() => of(due))
    );
  }


  /** fix rounding difference */
  public fixDifference(titles: CashTitle[], invoiceValue: number): CashTitle[] {
    if (!titles.length) {
      return []
    }
    let sum = 0;
    for (const title of titles) {
      sum += title.value;
    }
    const difference = Number((invoiceValue - sum).toFixed(2));
    titles[0].value = Number(((titles[0].value || 0) + difference).toFixed(2));
    if (titles[0].balance !== undefined) {
      titles[0].balance = Number(((titles[0].balance || 0) + difference).toFixed(2));
    }
    return titles;
  }

  /**
   * @deprecated
   */
  async assemblyTitlesFromInvoice(invoice: Invoice, finance: Finance, titleType: string, expenseType: string, os: MkgOS): Promise<CashTitle[]> {
    let titles = [];
    for (const parcel of finance.paymentCondition.parcels) {
      const titleValue = Utilities.calcPercentage(parcel.percentage, finance.value);
      let title: CashTitle = {
        status: 1,
        value: titleValue,
        movementDate: new Date(invoice.dispatchDate),
        expirationDate: await firstValueFrom(this.getExpirationDate(parcel.days)),
        typeId: titleType,
        supplier_clientId: invoice.sender,
        paymentConditionId: finance.paymentCondition._id,
        expenseTypeId: expenseType,
        parcel: parcel.seq,
        invoiceNumber: invoice.number,
        serial: invoice.serial,
        balance: titleValue,
        adiantamento: false,
        rateTitle: 0
      }
      if (os && os.codeSystem) {
        title.companyCode = os.codeSystem.toString();
        title.idOS = os.id;
      }
      titles.push(title)
    }

    return this.fixDifference(titles, finance.value);
  }

  /**
  * @deprecated
  */
  async assemblyTitlesFromNF(invoice: Pick<NF_response, "numero" | "serie">, finance: Finance, titleType: string, expenseType: string, clientId?: string): Promise<CashTitle[]> {
    let titles = [];

    for (const parcel of finance.paymentCondition.parcels) {
      const titleValue = Utilities.calcPercentage(parcel.percentage, finance.value);
      let title: CashTitle = {
        status: 1,
        value: titleValue,
        movementDate: new Date(),
        expirationDate: await firstValueFrom(this.getExpirationDate(parcel.days)),
        typeId: titleType,
        paymentConditionId: finance.paymentCondition._id,
        expenseTypeId: expenseType || "",
        parcel: parcel.seq,
        invoiceNumber: invoice.numero.toString(),
        serial: invoice.serie.toString(),
        balance: titleValue,
        adiantamento: false,
        rateTitle: 0
      }
      if (clientId) {
        title['supplier_clientId'] = clientId;
      }
      titles.push(title)
    }

    return this.fixDifference(titles, finance.value);
  }

  /**
   * @deprecated
   */
  async assemblyTitlesFromNFSE(invoice: NFSe, finance: Finance, titleType: string, expenseType: string, clientId?: string): Promise<CashTitle[]> {
    let titles = [];

    for (const parcel of finance.paymentCondition.parcels) {
      const titleValue = Utilities.calcPercentage(parcel.percentage, finance.value);
      let title: CashTitle = {
        status: 1,
        value: titleValue,
        movementDate: new Date(),
        expirationDate: await firstValueFrom(this.getExpirationDate(parcel.days)),
        typeId: titleType,
        paymentConditionId: finance.paymentCondition._id,
        expenseTypeId: expenseType || "",
        parcel: parcel.seq,
        invoiceNumber: invoice.RPS[0].RPSNumero.toString(),
        serial: invoice.RPS[0].RPSSerie,
        balance: titleValue,
        adiantamento: false,
        rateTitle: 0
      }
      if (clientId) {
        title['supplier_clientId'] = clientId;
      }
      titles.push(title)
    }

    return this.fixDifference(titles, finance.value);
  }

  /**
   * @TODO Store the user selected finances into invoice
   * @param nfe The NFe, attention: NFCe hanven't tag <cobr>
   * @param args
   * @returns The title to create
   */
  assemblyTitlesFromDuplicates(nfe: NFe, args: {
    operation: Operation,
    supplier_clientId: ObjectId,
    paymentConditionId: ObjectId
  }) {
    const titles: CashTitle[] = [];

    for (const dup of nfe.cobr.dup) {
      titles.push({
        status: 1,
        value: dup.vDup,
        balance: dup.vDup,
        adiantamento: false,
        discount: 0,
        movementDate: new Date(nfe.ide.dhEmi),
        expirationDate: moment(dup.dVenc).endOf("day").toDate(), // avoiding fuso
        invoiceNumber: `${nfe.ide.nNF}`,
        serial: `${nfe.ide.serie}`,
        parcel: Number(dup.nDup),
        received: [],
        qtdaPortion: nfe.cobr.dup.length,
        typeId: args.operation.titleTypeId,
        expenseTypeId: args.operation.expenseTypeId,
        supplier_clientId: args.supplier_clientId,
        paymentConditionId: args.paymentConditionId
      })
    }

    return titles;
  }

  /**
   * @TODO Store the user selected finances into invoice
   * @returns The titles to create
   */
  assemblyTitlesFromRPS(rps: RPS, args: {
    operation: Operation,
    supplier_clientId: ObjectId,
    paymentConditionId: ObjectId
  }): CashTitle[] {
    const titles: CashTitle[] = [];

    for (let parcel of rps.ListaParcelas) {

      titles.push({
        status: 1,
        value: Number(parcel.PrcValor),
        balance: Number(parcel.PrcValor),
        adiantamento: false,
        discount: 0,
        movementDate: new Date(rps.dCompetencia),
        expirationDate: moment(parcel.PrcDtaVencimento).endOf("day").toDate(),
        invoiceNumber: `${rps.RPSNumero}`,
        serial: `${rps.RPSSerie}`,
        parcel: Number(parcel.PrcNroFatura),
        received: [],
        qtdaPortion: rps.ListaParcelas.length,
        typeId: args.operation.titleTypeId,
        expenseTypeId: args.operation.expenseTypeId,
        supplier_clientId: args.supplier_clientId,
        paymentConditionId: args.paymentConditionId
      })
    }

    return titles;
  }
  /**
   * Apply low (payment/receipt) to a title
   * 
   * @param titleId - ID of the title
   * @param receive - Receive object with payment details
   * @param cardAdminInfo - Card admin configuration (optional)
   * @param typeCalculateLow - Type of calculation (DISCOUNT, SURCHARGE, etc)
   * @returns Promise with the updated title and related data
   */
  async applyLow(
    titleId: string,
    receive: Receive,
    cardAdminInfo?: any,
    typeCalculateLow?: ApplyLowOptions,
    cnpj?: string
  ): Promise<ApplyLowResponse> {
    const url = `${environment.mkgoURL}/api/v1/title/${titleId}/apply-low`;
    const companyCnpj = cnpj || this.dataService.company.cnpj;
    const header = await firstValueFrom(this.dataService.httpOptions(companyCnpj));
    const mappedType = typeCalculateLow && ApplyLowOptions[typeCalculateLow] || "NONE";

    return firstValueFrom(
      this.http.post<ApplyLowResponse>(url, { receive, cardAdminInfo, typeCalculateLow: mappedType }, header)
    );
  }

  /**
   * Apply multiple lows
   * 
   * @param titles - Array of titles with their receive data
   * @param cnpj - Optional CNPJ to override the default company
   * @returns Promise with the results of all operations
   */
  async applyMultipleLows(
    titles: Array<{
      titleId: string;
      receive: Receive;
      cardAdminInfo?: any;
      typeCalculateLow?: ApplyLowOptions;
    }>,
    cnpj?: string
  ): Promise<MultipleApplyLowResponse> {
    const url = `${environment.mkgoURL}/api/v1/title/apply-multiple-lows`;
    const companyCnpj = cnpj || this.dataService.company.cnpj;
    const header = await firstValueFrom(this.dataService.httpOptions(companyCnpj));

    // Map ApplyLowOptions enum values to string values for API
    const mappedTitles = titles.map(t => {
      const mappedType = t.typeCalculateLow && ApplyLowOptions[t.typeCalculateLow] || "NONE";
      return { ...t, typeCalculateLow: mappedType };
    });

    return firstValueFrom(
      this.http.post<MultipleApplyLowResponse>(url, { titles: mappedTitles }, header)
    );
  }

  async cancelLow(
    titleId: string,
    receiveId: string,
    options?: { reopenConciliations?: boolean },
    cnpj?: string
  ): Promise<CancelLowResponse> {
    const url = `${environment.mkgoURL}/api/v1/title/${titleId}/cancel-low`;
    const companyId = cnpj || this.dataService.company.cnpj;
    const header = await firstValueFrom(this.dataService.httpOptions(companyId));
    const body = {
      receiveId,
      reopenConciliations: options?.reopenConciliations ?? false
    };

    return firstValueFrom(
      this.http.post<CancelLowResponse>(url, body, header)
    );
  }
}

export interface ApplyLowResponse {
  success: boolean;
  title: CashTitle;
  cashier?: any;
  bank?: any;
  performanceLog?: any;
}

export interface MultipleApplyLowResponse {
  success: boolean;
  processed: number;
  errors: number;
  results: Array<{
    titleId: string;
    success: boolean;
    title: CashTitle;
  }>;
  errorsList: Array<{
    titleId: string;
    error: string;
  }>;
}

export interface CancelLowResponse {
  success: boolean;
  title: CashTitle;
  cashier?: any;
  bank?: any;
  retroactive?: any;
  performanceLog?: any;
}
